import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { JsonHubProtocol, LogLevel } from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { Observable, ReplaySubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';
import { UserSessionService } from './user-session.service';

export type TaskEventUpdated = { changeoverId: string, taskId: string, taskEventId: string };
export type ChangeoverFinalised = { changeoverId: string };
export type CurrentChangeoverChanged = { changeoverId: string | null };

@Injectable({
  providedIn: 'root'
})
export class LiveUpdateService {
  private connection: signalR.HubConnection | null = null;

  private isConnectedChanged = new ReplaySubject<boolean>(1);
  connectionId: string | null = null;
  public get IsConnectedChanged(): Observable<boolean> {
    return this.isConnectedChanged;
  }

  private taskEventUpdated = new Subject<TaskEventUpdated>();
  public get TaskEventUpdated(): Observable<TaskEventUpdated> {
    return this.taskEventUpdated;
  }

  private changeoverFinalised = new Subject<ChangeoverFinalised>();
  public get ChangeoverFinalised(): Observable<ChangeoverFinalised> {
    return this.changeoverFinalised;
  }

  private currentChangeoverChanged = new Subject<CurrentChangeoverChanged>();
  public get CurrentChangeoverChanged(): Observable<CurrentChangeoverChanged> {
    return this.currentChangeoverChanged;
  }

  constructor(
    private apiService: ApiService,
    private userSessionService: UserSessionService,
  ) {
    userSessionService.isAuthorisedChanged.subscribe(async isAuthorised => await this.handleAuthorisationChange(isAuthorised));
    userSessionService.activeLineIdChanged.subscribe(async lineId => await this.handleActiveLineChange());
  }

  private handleAuthorisationChange = async (isAuthorised: boolean) => {
    if (isAuthorised) {
      await this.connectToSignalRHub();
      await this.registerForLiveUpdates();
    } else {
      await this.disconnectFromSignalRHub();
      this.userSessionService.refreshAuthorisation();
    }
  }

  private handleActiveLineChange = async () => {
    // we explicitly disconnect to unregister for updates from the previous line
    await this.disconnectFromSignalRHub();
    await this.connectToSignalRHub();
    await this.registerForLiveUpdates();
  }

  private connectToSignalRHub = async () => {
    this.connection =
      new signalR.HubConnectionBuilder()
        .withUrl(`${environment.apiUrl}/live/`, {
          accessTokenFactory: () => this.userSessionService.accessToken || '',
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: context => {
            return 1000 * Math.min(45, 2 * Math.pow(context.previousRetryCount, 2));
          }
        })
        .withHubProtocol(new JsonHubProtocol())
        .configureLogging(
          environment.isProduction
            ? LogLevel.Error
            : LogLevel.Warning)
        .build();

    this.connection.onreconnecting(() => this.isConnectedChanged.next(false));
    this.connection.onreconnected(connectionId => {
      const previousConnectionId = this.connectionId;
      this.connectionId = connectionId || null;

      this.registerForLiveUpdates(previousConnectionId);

      this.isConnectedChanged.next(true);
    });

    this.connection.on(
      'taskEventUpdated',
      (changeoverId: string, taskId: string, taskEventId: string) => this.taskEventUpdated.next({ changeoverId, taskId, taskEventId })
    );

    this.connection.on(
      'changeoverFinalised',
      (changeoverId: string) => this.changeoverFinalised.next({ changeoverId })
    );

    this.connection.on(
      'currentChangeoverChanged',
      (changeoverId: string) => this.currentChangeoverChanged.next({ changeoverId })
    );

    await this.connection.start();
    this.connectionId = this.connection.connectionId;
    this.isConnectedChanged.next(true);
  }

  private disconnectFromSignalRHub = async () => {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
  }

  private registerForLiveUpdates = async (previousConnectionId: string | null = null) => {
    if (this.connectionId) {
      await this.apiService.registerForLiveUpdates(
        this.userSessionService.activeLineId,
        this.userSessionService.username,
        this.connectionId,
        previousConnectionId);
    }
  }
}
